import type { DBSchema } from "idb";
import type { AvatarUrls } from "../avatarDisplay";

export const LEGACY_DB_NAME = "styx-v1";
export const GLOBAL_DB_NAME = "styx-global";
export const USER_DB_PREFIX = "styx-user-";
export const GLOBAL_DB_VER = 2;
export const USER_DB_VER = 4;
export const LEGACY_DB_VER = 2;
export const META_IDENTITY_KEY = "identity";
export const META_SYNC_SEQ_KEY = "sync.last_seq";
/** Per-device vault auto-lock: `{ minutes: number | null }` — null = never. */
export const META_VAULT_AUTOLOCK_KEY = "vault.autolock_minutes";

export type ContactRow = {
  user_id: string;
  username: string;
  public_key_ed: string;
  public_key_x: string;
  /** Back-compat: terminal URL for old snapshots; prefer `avatar`. */
  avatar_url?: string | null;
  avatar?: AvatarUrls | null;
};

export type IdentityRow = {
  userId: string;
  username: string;
  email: string;
  publicKeyEdB64: string;
  secretKeyEdB64: string;
};

export type MessageRow = {
  id: string;
  peerId: string;
  from_id: string;
  to_id: string;
  created_at: number;
  plaintext: string;
  pending?: boolean;
  /** True when message is in the offline outbox (relay not available); queued=false means WS send attempted. */
  queued?: boolean;
  /** True when outbox flush exceeded retry budget; user can tap to retry. */
  failed?: boolean;
  delivered?: boolean;
  read?: boolean;
  seenLocal?: boolean;
  /** DM sync / tombstone: server cleared ciphertext; UI shows deleted stub. */
  deleted?: boolean;
};

/** Pending send queue (IndexedDB). DM stores pre-encrypted WS payload; workspace stores plain body. */
export type OutboxEntry = {
  id: string;
  kind: "dm" | "workspace-topic";
  peerId: string;
  created_at: number;
  attempts: number;
  /** Workspace: WebSocket accepted the frame; do not re-send until ack (avoids duplicate server rows). */
  sentAtLeastOnce?: boolean;
  dmPayload?: {
    to: string;
    nonce: string;
    ciphertext: string;
    sender_nonce: string;
    sender_ciphertext: string;
    attachment_blob_ids?: string[];
  };
  wsPayload?: {
    workspace_id: string;
    topic_id: string;
    body: string;
    reply_to?: string;
  };
};

export type LinkPreviewCacheRow = {
  id: string;
  encrypted: ArrayBuffer;
  fetchedAt: number;
};

export type ImageAtRestCacheRow = {
  blobId: string;
  mime: string;
  encrypted: ArrayBuffer;
  byteLength: number;
  lastAccessAt: number;
};

export type SyncCursorRow = { value: number };

export interface StyxGlobalSchema extends DBSchema {
  meta: {
    key: string;
    value: IdentityRow;
  };
}

export interface StyxUserSchema extends DBSchema {
  meta: {
    key: string;
    value: SyncCursorRow;
  };
  contacts: {
    key: string;
    value: ContactRow;
    indexes: { "by-username": string };
  };
  messages: {
    key: string;
    value: MessageRow;
    indexes: { "by-peer": string; "by-peer-sort": [string, number, string] };
  };
  outbox: {
    key: string;
    value: OutboxEntry;
    indexes: { "by-peer-sort": [string, number, string] };
  };
  link_preview_cache: {
    key: string;
    value: LinkPreviewCacheRow;
  };
  image_atr_cache: {
    key: string;
    value: ImageAtRestCacheRow;
    indexes: { "by-lru": number };
  };
}

export interface StyxLegacySchema extends DBSchema {
  meta: {
    key: string;
    value: IdentityRow | SyncCursorRow;
  };
  contacts: {
    key: string;
    value: ContactRow;
    indexes: { "by-username": string };
  };
  messages: {
    key: string;
    value: MessageRow;
    indexes: { "by-peer": string };
  };
}
